import React, { useState } from 'react'
import '../../styles/pages.css'

/**
 * Confidence bar with color coding
 */
function ConfidenceBar({ label, value }) {
  const numValue = parseFloat(value) || 0
  const percentage = numValue * 100

  let color = 'var(--color-text-muted)'
  if (numValue >= 0.85) color = 'var(--color-success)'
  else if (numValue >= 0.7) color = 'var(--color-warning)'
  else if (numValue >= 0.5) color = '#f59e0b' // amber
  else color = 'var(--color-error)'

  return (
    <div style={{ marginBottom: 'var(--space-2)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span className="text-sm">{label}</span>
        <span className="text-sm" style={{ fontWeight: 500 }}>{percentage.toFixed(0)}%</span>
      </div>
      <div
        style={{
          height: 6,
          background: 'var(--color-bg-tertiary)',
          borderRadius: 3,
          overflow: 'hidden'
        }}
      >
        <div
          style={{
            width: `${percentage}%`,
            height: '100%',
            background: color,
            borderRadius: 3,
            transition: 'width 0.3s ease'
          }}
        />
      </div>
    </div>
  )
}

/**
 * Image gallery for source item
 */
function ImageGallery({ images }) {
  const [selectedIndex, setSelectedIndex] = useState(0)

  if (!images || images.length === 0) {
    return (
      <div
        style={{
          width: '100%',
          height: 300,
          background: 'var(--color-bg-tertiary)',
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--color-text-muted)'
        }}
      >
        No images available
      </div>
    )
  }

  return (
    <div>
      {/* Main image */}
      <div
        style={{
          width: '100%',
          height: 300,
          background: 'var(--color-bg-secondary)',
          borderRadius: 8,
          overflow: 'hidden',
          marginBottom: 'var(--space-2)'
        }}
      >
        <img
          src={images[selectedIndex]}
          alt={`Image ${selectedIndex + 1}`}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain'
          }}
        />
      </div>

      {/* Thumbnails */}
      {images.length > 1 && (
        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          {images.map((img, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => setSelectedIndex(idx)}
              style={{
                width: 60,
                height: 60,
                padding: 0,
                border: idx === selectedIndex ? '2px solid var(--color-primary)' : '2px solid transparent',
                borderRadius: 4,
                overflow: 'hidden',
                cursor: 'pointer',
                background: 'var(--color-bg-secondary)'
              }}
            >
              <img
                src={img}
                alt={`Thumbnail ${idx + 1}`}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function ReviewProposal({ proposal, loading, onAccept, onDismiss, onClose }) {
  const [actionLoading, setActionLoading] = useState(null)

  if (!proposal) return null

  const sourceItem = proposal.source_item || {}
  const proposed = proposal.proposed_data || {}
  const isReviewed = proposal.status !== 'needs_review'

  async function handleAccept() {
    setActionLoading('accept')
    try {
      await onAccept(proposal.proposal_id)
    } finally {
      setActionLoading(null)
    }
  }

  async function handleDismiss() {
    setActionLoading('dismiss')
    try {
      await onDismiss(proposal.proposal_id)
    } finally {
      setActionLoading(null)
    }
  }

  return (
    <div
      className="modal-overlay"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 'var(--space-4)'
      }}
    >
      <div
        className="modal-content card"
        style={{
          width: '100%',
          maxWidth: 900,
          maxHeight: '90vh',
          overflow: 'auto',
          position: 'relative'
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 'var(--space-4)',
          paddingBottom: 'var(--space-3)',
          borderBottom: '1px solid var(--color-border)'
        }}>
          <div>
            <h2 style={{ margin: 0, marginBottom: 4 }}>Review Proposal</h2>
            <span className="text-sm text-muted">ID: {proposal.proposal_id}</span>
            {isReviewed && (
              <span
                className={`badge ${proposal.status === 'manually_added_after_review' ? 'badge-success' : 'badge-error'}`}
                style={{ marginLeft: 'var(--space-2)' }}
              >
                {proposal.status === 'manually_added_after_review' ? 'Accepted' : 'Dismissed'}
              </span>
            )}
          </div>
          <button
            type="button"
            className="btn btn-sm"
            onClick={onClose}
            style={{ padding: '4px 8px' }}
          >
            &times;
          </button>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
            <div className="loading-spinner" />
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-6)' }}>
            {/* Left: Source Item */}
            <div>
              <h3 style={{ marginTop: 0, marginBottom: 'var(--space-3)' }}>Source Item</h3>

              <ImageGallery images={sourceItem.image_urls} />

              <div style={{ marginTop: 'var(--space-4)' }}>
                <div style={{ marginBottom: 'var(--space-2)' }}>
                  <span className="text-sm text-muted">Item ID</span>
                  <div style={{ fontFamily: 'monospace' }}>{sourceItem.item_id}</div>
                </div>

                <div style={{ marginBottom: 'var(--space-2)' }}>
                  <span className="text-sm text-muted">Title</span>
                  <div style={{ fontWeight: 500 }}>{sourceItem.title}</div>
                </div>

                {sourceItem.description && (
                  <div>
                    <span className="text-sm text-muted">Description</span>
                    <div
                      className="text-sm"
                      style={{
                        maxHeight: 100,
                        overflow: 'auto',
                        background: 'var(--color-bg-secondary)',
                        padding: 'var(--space-2)',
                        borderRadius: 4,
                        marginTop: 4
                      }}
                    >
                      {sourceItem.description}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right: Proposed Data + Confidence */}
            <div>
              <h3 style={{ marginTop: 0, marginBottom: 'var(--space-3)' }}>AI Proposal</h3>

              {/* Proposed fields */}
              <div
                style={{
                  background: 'var(--color-bg-secondary)',
                  padding: 'var(--space-4)',
                  borderRadius: 8,
                  marginBottom: 'var(--space-4)'
                }}
              >
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
                  <div>
                    <span className="text-sm text-muted">Brand</span>
                    <div style={{ fontWeight: 500, fontSize: 16 }}>{proposed.brand || '-'}</div>
                  </div>
                  <div>
                    <span className="text-sm text-muted">Model</span>
                    <div style={{ fontWeight: 500, fontSize: 16 }}>{proposed.model || '-'}</div>
                  </div>
                </div>

                <div style={{ marginTop: 'var(--space-3)' }}>
                  <span className="text-sm text-muted">Category</span>
                  <div style={{ fontWeight: 500, fontSize: 14 }}>{proposed.category_path || '-'}</div>
                </div>

                {proposed.variant_dimensions && Object.keys(proposed.variant_dimensions).length > 0 && (
                  <div style={{ marginTop: 'var(--space-3)' }}>
                    <span className="text-sm text-muted">Variant Dimensions</span>
                    <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', marginTop: 4 }}>
                      {Object.entries(proposed.variant_dimensions).map(([key, val]) => (
                        <span key={key} className="badge badge-neutral">
                          {key}: {val}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Confidence scores */}
              <h4 style={{ marginBottom: 'var(--space-3)' }}>Confidence Scores</h4>
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <ConfidenceBar label="Brand" value={proposal.brand_confidence} />
                <ConfidenceBar label="Model" value={proposal.model_confidence} />
                <ConfidenceBar label="Category" value={proposal.category_confidence} />
                <ConfidenceBar label="Variant" value={proposal.variant_confidence} />
                <div style={{ marginTop: 'var(--space-3)', paddingTop: 'var(--space-3)', borderTop: '1px solid var(--color-border)' }}>
                  <ConfidenceBar label="Overall" value={proposal.overall_confidence} />
                </div>
              </div>

              {/* Image used indicator */}
              <div style={{ marginBottom: 'var(--space-3)' }}>
                <span className="text-sm text-muted">Image Analysis</span>
                <div>
                  {proposal.image_used ? (
                    <span className="badge badge-success">Image used</span>
                  ) : (
                    <span className="badge badge-neutral">Text only</span>
                  )}
                </div>
              </div>

              {/* AI Reasoning */}
              {proposal.ai_reasoning && (
                <div>
                  <span className="text-sm text-muted">AI Reasoning</span>
                  <div
                    className="text-sm"
                    style={{
                      background: 'var(--color-bg-tertiary)',
                      padding: 'var(--space-3)',
                      borderRadius: 4,
                      marginTop: 4,
                      maxHeight: 150,
                      overflow: 'auto',
                      fontStyle: 'italic'
                    }}
                  >
                    {proposal.ai_reasoning}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        {!isReviewed && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 'var(--space-3)',
              marginTop: 'var(--space-6)',
              paddingTop: 'var(--space-4)',
              borderTop: '1px solid var(--color-border)'
            }}
          >
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={actionLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn"
              style={{ background: 'var(--color-error)', color: 'white' }}
              onClick={handleDismiss}
              disabled={actionLoading}
            >
              {actionLoading === 'dismiss' ? 'Dismissing...' : 'Dismiss (Bad Data)'}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleAccept}
              disabled={actionLoading}
            >
              {actionLoading === 'accept' ? 'Accepting...' : 'Accept & Create'}
            </button>
          </div>
        )}

        {/* Reviewed info */}
        {isReviewed && proposal.reviewed_at && (
          <div
            style={{
              marginTop: 'var(--space-4)',
              paddingTop: 'var(--space-3)',
              borderTop: '1px solid var(--color-border)',
              textAlign: 'right'
            }}
            className="text-sm text-muted"
          >
            Reviewed by {proposal.reviewed_by} on {new Date(proposal.reviewed_at).toLocaleString()}
          </div>
        )}
      </div>
    </div>
  )
}

export default ReviewProposal
